import { Box, DialogContent, Tooltip } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { UserRoles } from "../../../shared/constants/enums";
import { Button } from "../../../shared/ReusableComponents";
import IconButton from "../../../shared/ReusableComponents/IconButton";
import Input from "../../../shared/ReusableComponents/Input";
import { NewOffsetInput } from "../../../shared/ReusableComponents/NewOffsetInput";

const style = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-disabled": {
      backgroundColor: "#F5F5F5",
      "& fieldset": {
        borderColor: "#B1B1B1",
      },
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#000", // normal black border on hover
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#000", // normal black on focus
    },
  },
  "& .Mui-error": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--color-error-main)", // default to black
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--color-error-main)", // red on hover only if error
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--color-error-main)", // red on focus if error
    },
  },
};

export const FluidOrderItem = ({
  fields,
  maxItems,
  formDataList,
  setFormDataList,
  addRow,
  deleteRow,
  onCancel,
  handleCreate,
  offsetList,
  setOffsetList,
  isEditMode,
  onClickEditMode,
  offsetErrors,
  setOffsetErrors,
  formErrorsList,
  setFormErrorsList,
  isCreated,
}: {
  fields: any;
  maxItems: number;
  formDataList: any;
  setFormDataList: any;
  addRow: () => void;
  deleteRow: any;
  onCancel: () => void;
  handleCreate: any;
  offsetList: any;
  setOffsetList: any;
  isEditMode: boolean;
  onClickEditMode: any;
  offsetErrors: any;
  setOffsetErrors: any;
  formErrorsList: any;
  setFormErrorsList: any;
  isCreated: boolean;
}) => {
  const auth = useSelector((state: any) => state.login);
  const isCoordinate = auth?.userInfo?.roleId === UserRoles.SUBJECT_COORDINATOR;
  const isSuperUser = auth?.userInfo?.roleId === UserRoles.SUPER_USER;

  return (
    <React.Fragment>
      <Box>
        <div className="grey-background">
          {formDataList.map((formData: any, index: any) => (
            <DialogContent
              key={index}
              sx={{
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                marginTop: "11px",
                padding: "11px 18px 11px 17px",
              }}
            >
              <Box sx={style}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <div
                    style={{
                      display: "inline-block",
                      maxWidth: "310px",
                    }}
                  >
                    <NewOffsetInput
                      isSingleLineLayout={true}
                      onValueChanged={(e) => {
                        const updatedOffsets = [...offsetList];
                        updatedOffsets[index] = e;
                        setOffsetList(updatedOffsets);

                        const updatedErrors = [...offsetErrors];
                        // if (e === 0) {
                        //   updatedErrors[index] = "Please enter valid input.";
                        // } else {
                        updatedErrors[index] = "";
                        // }
                        setOffsetErrors(updatedErrors);
                      }}
                      value={offsetList[index]}
                      errorMessage={offsetErrors[index]}
                      onlyHighlightOnError={false}
                      disabled={!isEditMode || isCoordinate}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      marginTop: "-30px",
                    }}
                  >
                    {formDataList.length > 1 && isEditMode && !isCoordinate && (
                      <IconButton
                        text="Remove"
                        variant="text"
                        icon={
                          <span className="material-symbols-outlined">
                            delete
                          </span>
                        }
                        onClick={() => deleteRow(index)}
                      />
                    )}

                    {index === formDataList.length - 1 &&
                      isEditMode &&
                      formDataList.length < maxItems &&
                      !isCoordinate && (
                        <IconButton
                          text="Add New"
                          variant="text"
                          icon={
                            <span className="material-symbols-outlined">
                              add_circle
                            </span>
                          }
                          onClick={addRow}
                        />
                      )}
                  </div>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mt: "10px" }}>
                  {fields.map((field: any) => {
                    const labelLength = field.label.length;
                    // Dynamically calculate width
                    const baseWidth = labelLength * 10 + 80; // Adjust the multiplier as needed
                    const inputWidth = Math.min(Math.max(baseWidth, 120), 280); // made width between 120px and 280px

                    const isPrescribedByField = field.key === "prescribedBy";

                    // Check if other fields have data (excluding prescribedBy itself)
                    const otherFieldsHaveData = Object.entries(formData).some(
                      ([key, val]) =>
                        key !== "prescribedBy" &&
                        val !== "" &&
                        val !== null &&
                        val !== undefined
                    );

                    const fieldValue =
                      isPrescribedByField && isCoordinate
                        ? otherFieldsHaveData
                          ? formData[field.key]
                          : ""
                        : formData[field.key];

                    const inputValue =
                      fieldValue !== null && fieldValue !== undefined
                        ? String(fieldValue)
                        : "";

                    return (
                      <Box
                        key={field.key}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flexGrow: 1,
                          width: `${inputWidth}px`,
                        }}
                      >
                        <Input
                          name={field.key}
                          key={field.key}
                          label={
                            <Tooltip title={field.label}>
                              <span
                                style={{
                                  color: formErrorsList[index]?.[field.key]
                                    ? "var(--color-error-main)"
                                    : !isEditMode || isCoordinate
                                    ? "rgba(0, 0, 0, 0.38)"
                                    : "inherit",
                                }}
                              >
                                {field.label}
                                {!["additiveAmount", "rate"].includes(
                                  field.key
                                ) && (
                                  <span style={{ color: "inherit" }}> *</span>
                                )}
                              </span>
                            </Tooltip>
                          }
                          value={inputValue}
                          // size="small"
                          onChange={(e) => {
                            const { value } = e.target;

                            if (field.key === "volume" && value) {
                              if (/[^0-9.]/.test(value)) return;

                              if ((value.match(/\./g) || []).length > 1) return;

                              const [_, decimalPart] = value.split(".");
                              if (decimalPart && decimalPart.length > 2) return;

                              if (value.length > 9) return;
                            }

                            // Update the form data list with the new value
                            const updatedList = [...formDataList];
                            updatedList[index][field.key] = value;
                            setFormDataList(updatedList);

                            // Create a copy of the form errors list
                            const updatedFormErrors = [...formErrorsList];
                            const fieldErrors = {
                              ...(updatedFormErrors[index] || {}),
                            };

                            // Check if the field value is not empty
                            if (value.trim() === "") {
                              // If the value is cleared, show an error only if it was previously set
                              if (
                                formDataList[index][field.key] &&
                                value === ""
                              ) {
                                fieldErrors[field.key] = true;
                              } else {
                                delete fieldErrors[field.key]; // Clear error if the field is empty but never touched
                              }
                            } else {
                              delete fieldErrors[field.key]; // Clear error if the field is filled
                            }

                            // Update the errors list
                            updatedFormErrors[index] = fieldErrors;
                            setFormErrorsList(updatedFormErrors);
                          }}
                          disabled={
                            !isEditMode ||
                            field.key === "prescribedBy" ||
                            isCoordinate
                          }
                          error={!!formErrorsList[index]?.[field.key]}
                          // sx={{
                          //   width: `${inputWidth}px`,
                          //   flexGrow: 1,
                          //   flexShrink: 1,
                          // }}
                        />
                        {formErrorsList[index]?.[field.key] && (
                          <span
                            style={{
                              color: "var(--color-error-main)",
                              fontSize: "0.75rem",
                              marginTop: "4px",
                            }}
                          >
                            Fields with * are mandatory
                          </span>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                {/* <p
                  style={{
                    color: "var(--color-error-main)",
                    fontSize: "0.875rem",
                  }}
                >
                  {formErrorsList[index] &&
                    Object.keys(formErrorsList[index]).length > 0 &&
                    "All the fields are mandatory, Please check."}
                </p> */}
              </Box>
            </DialogContent>
          ))}

          <Box
            sx={{
              width: "100%",
              backgroundColor: "#fff",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: "20px",
            }}
          >
            {isSuperUser && (
              <React.Fragment>
                {isEditMode ? (
                  <>
                    <Button
                      text="Cancel"
                      fullWidth={false}
                      variant="outlined"
                      type="button"
                      onClick={onCancel}
                    />
                    <Button
                      text={isCreated ? "Update" : "Create"}
                      fullWidth={false}
                      className="ml-1"
                      onClick={handleCreate}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      text="Edit"
                      fullWidth={false}
                      variant="outlined"
                      onClick={() => onClickEditMode(true)}
                    />
                  </>
                )}
              </React.Fragment>
            )}
          </Box>
        </div>
      </Box>
    </React.Fragment>
  );
};
